import React, {useEffect, useState} from "react";
import { Form, Input, Button, Select, message, Checkbox } from "antd";
import { createUser, updateUserById } from "../../../../Services/UserService";

const { Option } = Select;

const UserCreationForm = ({
  history,
  userData,
  callReFetch,
  onClose,
  DeptData,
  CounterData,
  setSelectedDept,
  callReFetchCounter,
  initialValues,
  isEditMode = false,
}) => {
  const [form] = Form.useForm();
  const [selectedRoles, setSelectedRoles] = useState([]);

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue({
        ...initialValues,
        // Exclude password field in edit mode
      });
      setSelectedRoles(initialValues.roles || []);
    }
  }, [initialValues, form]);

  const onFinish = async (values) => {
    try {
      if (isEditMode && initialValues?._id) {
        await updateUserById(initialValues._id, values);
        message.success("User updated successfully");
      } else {
        await createUser(values);
        message.success("User created successfully");
      }
      form.resetFields();
      callReFetch();
      onClose();
    } catch (error) {
      message.error(
        error?.response?.data?.message ||
          `Something went wrong, failed to ${isEditMode ? 'update' : 'create'} user`
      );
    }
  };

const ALL_ROLE_OPTIONS = [
  { label: "Admin", value: "admin" },
  { label: "SPG", value: "spg" },
  { label: "Super SPG", value: "superspg" },
  { label: "Office", value: "office" },
  { label: "Area Representative", value: "arearep" },
  { label: "Staff Admin Internal", value: "internaladmin" },
  { label: "Staff Admin Gudang", value: "gudangadmin" },
  { label: "Staff Admin Logistik", value: "logistikadmin" },
];

const ROLE_OPTIONS = userData?.roles?.includes("internaladmin")
  ? ALL_ROLE_OPTIONS.filter((role) =>
      ["spg", "arearep", "superspg"].includes(role.value)
    )
  : ALL_ROLE_OPTIONS;

  const handleDeptChange = (value) => {
    setSelectedDept(value);
    callReFetchCounter();
    // Only reset counter code if not arearep user or if department is cleared
    if (!selectedRoles.includes("arearep") || !value) {
      form.setFieldsValue({ counterCode: undefined });
    }
  };

  const handleRoleChange = (checkedValues) => {
    const hasSuperSPG = checkedValues.includes("superspg");
    const nextRoles = hasSuperSPG ? ["superspg"] : checkedValues;

    setSelectedRoles(nextRoles);

    form.setFieldsValue({
      roles: nextRoles,
      counterCode: undefined,
      dept: hasSuperSPG ? undefined : form.getFieldValue("dept"),
    });
  };

  const isAreaRep = selectedRoles.includes("arearep");
  const isSuperSPG = selectedRoles.includes("superspg");

  // Filter role options:
  // - If Super SPG is selected: only show Super SPG
  // - If no roles selected: show all roles including Super SPG
  // - If other roles selected: hide Super SPG
  const filteredRoleOptions = isSuperSPG
    ? ROLE_OPTIONS.filter((role) => role.value === "superspg")
    : selectedRoles.length === 0
    ? ROLE_OPTIONS // Show all if nothing selected
    : ROLE_OPTIONS.filter((role) => role.value !== "superspg"); // Hide Super SPG if other roles selected

  return (
    <Form form={form} layout="vertical" onFinish={onFinish}>
      {isEditMode && (
        <Form.Item name="isDeleted" label="Active Status" initialValue={false}>
          <Select>
            <Option value={false}>Active</Option>
            <Option value={true}>Inactive</Option>
          </Select>
        </Form.Item>
      )}

      <Form.Item
        name="username"
        label="Username"
        rules={[{ required: true, message: "Please input username!" }]}
      >
        <Input placeholder="Username" />
      </Form.Item>

      {!isEditMode && (
        <Form.Item
          name="password"
          label="Password"
          rules={[{ required: true, message: "Please input password!" }]}
        >
          <Input.Password placeholder="Password" />
        </Form.Item>
      )}

      <Form.Item
        name="name"
        label="Full Name"
        rules={[{ required: true, message: "Please input full name!" }]}
      >
        <Input placeholder="Full Name" />
      </Form.Item>

      {!isSuperSPG && (
        <>
          <Form.Item
            name="dept"
            label="Department"
            rules={[
              {
                required: !isAreaRep,
                message: "Please select department!",
              },
            ]}
          >
            <Select 
              placeholder="Select department" 
              onChange={handleDeptChange}
              allowClear={isAreaRep}
            >
              {DeptData?.map((dept) => (
                <Option key={dept.name} value={dept.name}>
                  {dept.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="counterCode"
            label="Counter Code"
            rules={[
              {
                required: !isAreaRep,
                message: "Please input counter code!",
              },
            ]}
          >
            <Select
              placeholder="Select counter code"
              disabled={!isAreaRep && !form.getFieldValue("dept")}
              showSearch
              mode={isAreaRep ? "multiple" : undefined}
              optionFilterProp="children"
              filterOption={(input, option) => {
                const children = option.children || "";
                return String(children).toLowerCase().includes(input.toLowerCase());
              }}
            >
              {Array.isArray(CounterData) &&
                CounterData.map((counter) => (
                  <Option key={counter.code} value={counter.code}>
                    {counter.code} - {counter.name} {counter.dept ? `(${counter.dept})` : ''}
                  </Option>
                ))}
            </Select>
          </Form.Item>
        </>
      )}

      <Form.Item name="roles" label="Roles">
        <Checkbox.Group options={filteredRoleOptions} onChange={handleRoleChange} />
      </Form.Item>

      <Form.Item>
        <Button type="primary" htmlType="submit">
          {isEditMode ? "Update User" : "Create User"}
        </Button>
      </Form.Item>
    </Form>
  );
};

export default UserCreationForm;